import React, { useRef, useEffect, useCallback } from "react";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    LineElement,
    PointElement,
    LinearScale,
    Title,
    Tooltip,
    Legend,
    CategoryScale,
    Filler,
} from "chart.js";

ChartJS.register(
    LineElement,
    PointElement,
    LinearScale,
    Title,
    Tooltip,
    Legend,
    CategoryScale,
    Filler
);

const WorkFlowTimeLineChart = (prop) => {
    const tickFontSize = prop?.tickFontSize || 8;
    const pxPerTick = 50;
    const visibleTicks = 20;

    const labels =
        prop?.data && prop?.data["Chart Label"]
            ? prop?.data["Chart Label"].split(",")?.length > 1
                ? prop?.data["Chart Label"].split(",")
                : ["", ...prop?.data["Chart Label"].split(",")]
            : [0, 0, 0, 0, 0];

    const totalTicks = labels.length;
    const needsScroll = totalTicks > visibleTicks;
    const canvasWidth = needsScroll ? totalTicks * pxPerTick : undefined;

    const options = {
        responsive: !needsScroll,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                display: true,
            },
        },
        scales: {
            x: {
                display: true,
                grid: {
                    display: true,
                    offset: false,
                },
                ticks: {
                    align: "center",
                    autoSkip: false,
                    maxRotation: 0,
                    minRotation: 0,
                    font: { size: tickFontSize },
                },
            },
            y: {
                display: true,
                grid: {
                    display: false,
                },
                ticks: {
                    font: { size: tickFontSize },
                },
            },
        },
    };

    const metricColorMap = {
        "CPU Usage": {
            borderColor: " #75BDE7",
            backgroundColor: "rgba(117, 189, 231, 0.2)",
        },
        "Memory Usage": {
            borderColor: " #BC6CD4",
            backgroundColor: "rgba(188, 108, 212, 0.2)",
        },
        "Network Usage": {
            borderColor: " #D89965",
            backgroundColor: "rgba(216, 153, 101, 0.2)",
        },
    };

    const buildDataset = (metricName) => ({
        label: metricName,
        data:
            prop?.data && prop?.data[metricName]
                ? prop?.data[metricName]?.split(",")?.length > 1
                    ? prop?.data[metricName]?.split(",")
                    : [0, ...prop?.data[metricName]?.split(",")]
                : [0, 0, 0, 0, 0],
        borderColor: metricColorMap[metricName]?.borderColor,
        backgroundColor: metricColorMap[metricName]?.backgroundColor,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
    });

    const datasets = prop?.params
        ? [buildDataset(prop.params)]
        : [
            buildDataset("CPU Usage"),
            buildDataset("Memory Usage"),
            buildDataset("Network Usage"),
        ];

    const chartRef = useRef(null);
    const axisRef = useRef(null);

    const copyYAxis = useCallback(() => {
        const chart = chartRef.current;
        const axisCanvas = axisRef.current;
        if (!chart || !axisCanvas) return;

        const sourceCanvas = chart.canvas;
        const yAxisWidth = chart.chartArea.left;
        const chartHeight = sourceCanvas.height;

        axisCanvas.width = yAxisWidth;
        axisCanvas.height = chartHeight;
        axisCanvas.style.width = yAxisWidth + "px";
        axisCanvas.style.height = chartHeight + "px";

        const axisCtx = axisCanvas.getContext("2d");
        axisCtx.clearRect(0, 0, yAxisWidth, chartHeight);
        axisCtx.drawImage(
            sourceCanvas,
            0, 0, yAxisWidth, chartHeight,
            0, 0, yAxisWidth, chartHeight
        );

        if (prop?.onReady) prop.onReady();
    }, [prop?.onReady]);

    const data = {
        labels,
        datasets,
    };

    if (needsScroll) {
        const scrollOptions = {
            ...options,
            responsive: false,
            animation: {
                onComplete: copyYAxis,
            },
        };

        return (
            <div className="chartWrapper" style={{ position: "relative", width: "100%", height: 120 }}>
                <div className="chartAreaWrapper" style={{
                    overflowX: "auto",
                    overflowY: "hidden",
                    width: "100%",
                    height: "100%",
                }}>
                    <div className="chartAreaWrapper2" style={{ width: canvasWidth, height: "100%" }}>
                        <Line ref={chartRef} data={data} options={scrollOptions} width={canvasWidth} height={120} />
                    </div>
                </div>
                <canvas ref={axisRef} style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    pointerEvents: "none",
                    zIndex: 2,
                    background: "#fff",
                }} />
            </div>
        );
    }

    const nonScrollOptions = {
        ...options,
        animation: {
            onComplete: () => { if (prop?.onReady) prop.onReady(); },
        },
    };

    return <Line data={data} options={nonScrollOptions} />;
};

export default WorkFlowTimeLineChart;